<template>
	<!-- 
		# MainCard

		V.1.3

		Limpieza general y optimizaciones menores

		V.1.2

		Buscador automático
		
		V.1.1

		Ahora la configuración de origen de datos y origen de índice, 
		junto a la coniguración de campos, se define fuera del objeto de exportación

		V.1.0

		Para la integración de imágenes requiere 
		**img-input-autoload** configurado en elements y
		el controlador UiController.php y UiModel.php en la API Base. **Además hace uso del 
		paginador para javascript implementado en PaginateController.php**

		Para la actualización de datos hace uso de StorageController.php 

		@Author Daniel Lepe 2021
	-->

	<b-card id="PresentacionesIndexMainCard" no-body>

		<!-- CARD HEADER -->
		<b-row align-h="end" slot="header">
			<b-col>
				<div tabindex="-1" role="group">
					<div role="group" class="input-group">
						<input type="text" v-model="term" @keyup.enter="refresh()"
							:placeholder="labelsCfg.searchPlaceholder" class="form-control" />
						<div class="input-group-append" v-if="!!term">
							<b-button type="button" variant="light" @click="term = null; refresh()">
								<i class="fa fa-times"></i>
							</b-button>
							<b-button type="button" :variant="variant" @click="refresh()">
								<i class="fa fa-search"></i>
							</b-button>
						</div>
					</div>
				</div>
			</b-col>
			<b-col md="4" v-if="false">
				<b-button block :variant="variant" @click="cleanFormAndOpenModal()" class="mb-0"> {{ labelsCfg.newLabel }}
				</b-button>
			</b-col>
		</b-row>
		<!-- CARD HEADER -->

		<spinner v-if="isLoading" />

		<!-- DATA TEMPLATE -->
		<template v-else-if="parseInt(data.count)">

			<b-table :fields="fields" responsive striped class="mb-0" :items="items" @sort-changed="sortChanged">

				<!-- START TEMPLATES BASICOS DE DATOS -->
				<template #cell(imagen_uri)="data">
					<div class="product-img">
						<img v-if="!!data.value" :src="data.value" />
						<img v-else src="https://api.supereasy.mx/storage/img/render/aab9ab5b8648335f07ad079381ff04a2/3" />
					</div>
					<!-- {{data.item.value}}  -->
					<!-- {{data.item.titulo_extendido}}  -->
				</template>
				<!-- START TEMPLATES BASICOS DE DATOS -->
				<template #cell(nombre)="data">
					{{ data.item.elemento }}
					<!-- {{data.item.titulo_extendido}}  -->
				</template>
				<template #cell(pres)="data">
					{{ data.item.presentacion }}
					{{ data.item.unidadmedicion }}
					<small>({{ data.item.tipopresentacion }})</small>
				</template>
				<template #cell(created)="data">{{ data.value | humanizar_fecha }} <br> <small>(Última actualizacion
						{{ data.item.modified | humanizar_diferencia }})</small> </template>
				<template #cell(modified)="data">{{ data.value | humanizar_diferencia }} <br> <small>(Última actualizacion
						{{ data.item.modified | humanizar_diferencia }})</small> </template>
				<template #cell(costo)="data">
					<strong>{{ data.value | currency }}</strong>
				</template>
				<template #cell(precio)="data">
					<strong>{{ data.value | currency }}</strong> <br>
					<small title="Precio restaurante">{{ data.item.precio_restaurante | currency }}</small>
				</template>
				<!-- END TEMPLATES BASICOS DE DATOS -->

				<!-- START OF AVATAR SAMPLES -->
				<template #cell(imagenes_id_agenda)="data">
					<api-img :comp="data.value" :rowed="true" />
				</template>
				<!-- END OF AVATAR SAMPLES -->

				<!-- START OF BOOLEAN SAMPLE -->
				<template #cell(disponibilidad)="data">
					<div class="align-center text-primary"
						v-if="data.item.visible_en_tienda == '1' && data.item.visible_en_zero == '1'">
						<i class="fa fa-eye"></i> Siempre
					</div>
					<div class="align-center text-warning"
						v-else-if="data.item.visible_en_tienda == '1' && data.item.visible_en_zero == '0'">
						<i class="fa fa-eye"></i> Solo con inventario
					</div>
					<div class="align-center text-danger" v-else-if="data.item.visible_en_tienda == '0'">
						<i class="fa fa-eye-slash"></i> Oculto
					</div>
				</template>
				<!-- END OF BOOLEAN SAMPLE -->

				<!-- START OF TOOLING -->
				<template #cell(_)="data">
					<b-button @click="setupItemInFormAndOpenModal(data.item)" size="sm" :variant="variant"> <i
							class="fa fa-fw fa-cog"></i> </b-button>
				</template>
				<!-- END OF TOOLING -->

			</b-table>

			<b-row slot="footer" align-v="center">

				<b-col>
					<strong>Página {{ data.page }} de {{ Math.ceil(data.count / data.per_page) }}</strong>
					(Se muestran <strong>{{ items.length }} registros</strong>
					de un total de {{ data.count }})
				</b-col>

				<b-col>
					<b-pagination class="mt-3" v-model="data.page" :total-rows="data.count" :per-page="data.per_page"
						size="sm" align="right" prev-text="Anterior" next-text="Siguiente" />
				</b-col>
			</b-row>
		</template>
		<!-- DATA TEMPLATE -->

		<!-- EMPTY JUMBOTRON -->
		<b-jumbotron class="mb-0" v-else>
			<template #header>{{ labelsCfg.emptyHeader }}</template>
			<template #lead>{{ labelsCfg.emptyLead }}</template>
		</b-jumbotron>
		<!-- EMPTY JUMBOTRON -->

		<!-- MAIN MODAL -->
		<b-modal id="main-card-modal" cancel-disabled ok-disabled scrollable :title="labelsCfg.newLabel" @hidden="hidden"
			size="md">

			<b-form @submit="onSubmit">
				<!-- IMAGEN URI -->
				<storage-img-uploader v-model="form.imagen_uri" label="Imágen de presentación" />
				<!-- IMAGEN URI -->

				<!-- TITULO EXTENDIDO -->
				<b-form-group label="Título" label-for="form-titulo_extendido">
					<b-form-input id="form-titulo_extendido" v-model="form.titulo_extendido" readonly />
				</b-form-group>
				<!-- TITULO EXTENDIDO -->

				<!-- DESCRIPCIÓN -->
				<b-form-group label="Descripcion:" label-for="form-descripcion">
					<b-form-textarea id="form-descripcion" v-model="form.descripcion" required></b-form-textarea>
				</b-form-group>
				<!-- DESCRIPCIÓN -->

				<b-row>
					<b-col>
						<b-form-group label="Marca" label-for="form-idmarca_fk">
							<!-- <b-form-input id="form-marca-hidden" v-model="form.marca" readonly /> -->
							<b-form-select id="form-idmarca_fk" v-model="form.idmarca_fk" :options="marcas" />
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Proveedor asignado" label-for="form-proveedor_asignado_id">
							<b-form-select id="form-proveedor_asignado_id" v-model="form.proveedor_asignado_id"
								:options="proveedores" />
						</b-form-group>
					</b-col>
				</b-row>

				<b-row>
					<b-col>
						<b-form-group label="Costo" label-for="form-costo">
							<b-form-input id="form-costo" v-model="form.costo" readonly />
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Precio" label-for="form-precio">
							<b-form-input id="form-precio" v-model="form.precio" readonly />
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Precio restaurante" label-for="form-precio_restaurante">
							<b-form-input id="form-precio_restaurante" v-model="form.precio_restaurante" />
						</b-form-group>
					</b-col>
				</b-row>

				<b-row>
					<b-col>
						<b-form-group label="Inventario" label-for="form-inventario">
							<b-form-input id="form-inventario" v-model="form.inventario" readonly />
						</b-form-group>
					</b-col>
					<b-col>
						<b-form-group label="Rendimiento" label-for="form-radio_rendimiento">
							<b-form-input id="form-radio_rendimiento" v-model="form.radio_rendimiento" />
						</b-form-group>
					</b-col>
				</b-row>

				<b-row>
					<b-col>
						<!-- visible_en_tienda -->
						<b-form-checkbox v-model="form.visible_en_tienda" name="check-button" value="1" unchecked-value="0"
							switch>
							<span v-if="form.visible_en_tienda == '1'">Disponible en tienda</span>
							<span v-else>Oculto la tienda</span>
						</b-form-checkbox>
						<!-- visible_en_tienda -->
					</b-col>
				</b-row>

				<b-row class="ml-1" v-if="form.visible_en_tienda == '1'">
					<b-col>
						<b-form-checkbox v-model="form.visible_en_zero" name="check-button" value="1" unchecked-value="0"
							switch>
							<span v-if="form.visible_en_zero == '1'">Disponible en inventario 0</span>
							<span v-else>Oculto en inventario 0</span>
						</b-form-checkbox>
						<b-form-checkbox v-model="form.promocionado_en_tienda" name="check-button" value="1"
							unchecked-value="0" switch>
							<span v-if="form.promocionado_en_tienda == '1'">Promocionado</span>
							<span v-else>Promover en la tienda</span>
						</b-form-checkbox>
						<b-form-checkbox v-model="form.promocionado_en_categoria" name="check-button" value="1"
							unchecked-value="0" switch>
							<span v-if="form.promocionado_en_categoria == '1'">Promocionado en su categoría</span>
							<span v-else>Promover en su categoria</span>
						</b-form-checkbox>
					</b-col>
				</b-row>


			</b-form>

			<template v-slot:modal-footer="{ cancel }">

				<b-button size="sm" variant="outline-danger" @click="tryToDelete(form.id)"
					v-if="!isProcessing && !!form.id">
					<i class="fa fa-trash"></i>
					{{ labelsCfg.btnDelete }}
				</b-button>

				<b-button size="sm" @click="cancel()" v-if="!isProcessing">
					{{ labelsCfg.btnCancel }}
				</b-button>

				<!-- Button with custom close trigger value -->
				<b-button size="sm" :variant="variant" @click="store()" v-if="!isProcessing">
					{{ labelsCfg.btnApply }}
				</b-button>

				<!-- Button with custom close trigger value -->
				<b-button size="sm" :variant="variant" disabled v-if="isProcessing">
					<span key="spin">
						<i class="fa fa-cog fa-spin"></i>
					</span>
					{{ labelsCfg.btnIsProcessing }}
				</b-button>

			</template>
		</b-modal>
		<!-- MAIN MODAL -->

	</b-card>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'
import swal from 'sweetalert'
import fkSelect from "@/views/elements/fk-select.vue";
import imgInputAutoload from "@/views/elements/img-input-autoload.vue"
import md5 from "js-md5";
import { uiAsidesFetch } from './archetype'

let labelsCfg = {
	emptyHeader: 'No hay presentaciones que mostrar',
	emptyLead: 'Intenta con otro término de búsqueda.',
	searchPlaceholder: 'Busca por marca, presentacion o categoría...',
	newLabel: 'Nuevo',
	btnDelete: 'Eliminar',
	btnCancel: 'Cancelar',
	btnApply: 'Aplicar',
	btnIsProcessing: 'Procesando...',
}

let indexSource = 'paginate/table/productos_overview',
	dataSource = 'define/source/presentacion'; // Para crear, actualizar y borrar
// dataSource = 'usuario/createagenda'; // Para crear, actualizar y borrar

let formArchetype = {
	titulo_extendido: null,
	marca: null,
	idmarca_fk: null,
	costo: null,
	precio: null,
	inventario: null,
	imagen_uri: null,
	descripcion: null,
	visible_en_zero: '1',
	visible_en_tienda: '1',
	promocionado_en_categoria: '0',
	promocionado_en_tienda: '0',
	precio_restaurante: null,
	radio_rendimiento: '1',
	proveedor_asignado_id: null
}

let fields = [ // DEJAR VACÍO PARA MOSTRAR TODO
	// {key: 'titulo_extendido'},
	{ key: 'imagen_uri', label: '' },
	{ key: 'nombre', label: 'Nombre', sortable: true },
	{ key: 'pres', label: 'Presentación', sortable: true },
	{ key: 'marca', sortable: true },
	{ key: 'costo', sortable: true },
	{ key: 'precio', sortable: true },
	{ key: 'inventario', sortable: true },
	{ key: 'disponibilidad', sortable: true },
	{ key: 'proveedorAsignado', label: 'Proveedor asignado', sortable: true },
	{ key: '_' }
]

export default {
	name: 'PresentacionesIndexMainCard',
	props: {},
	components: {
		fkSelect, imgInputAutoload
	},
	computed: {
		items() {
			return _.map(this.data.rows, item => {
				item._ = ' '
				return item
			})
		},
		dataMD5() {
			return md5(JSON.stringify(this.form));
		},
		// COMPUTA LA URI
		sourceURI() {
			let params = { sortBy: this.sorting.sortBy, sortDesc: this.sorting.sortDesc },
				searchParams = new URLSearchParams(params);

			let init = `${this.indexSource}/${this.data.per_page}/${this.data.page}`;

			if (!!this.term) {
				init += "/" + this.term;
			} else if (!this.term || this.term == "s") {
				init += "/@";
			}
			init += "/" + searchParams.toString().replaceAll(/\&/g, ':');
			return init;
		},
	},
	data: () => {
		return {
			isLoading: false,
			isProcessing: false,
			term: null,
			variant: "primary",
			dataSource,
			indexSource,
			fields,
			proveedores: [],
			marcas: [],
			unidadesMedicion: [],
			tiposPresentacion: [],
			labelsCfg: _.cloneDeep(labelsCfg),
			form: _.cloneDeep(formArchetype),
			data: {
				rows: [],
				count: 0,
				per_page: 10,
				page: 1
			},
			sorting: {
				sortBy: null,
				sortDesc: null
			}
		}
	},
	methods: {
		sortChanged(e){
			this.sorting = e;
			this.loadUp()
		},
		// PREGUNTA Y CONTINÚA CON EL BORRADO DEL REGISTRO
		tryToDelete(itemID) {
			if (!!itemID) {
				swal({
					text: '¿Estás seguro que deseas eliminar éste registro?',
					icon: 'info',
					buttons: {
						cancel: 'Cancelar',
						proceed: 'Si'
					}
				}).then(selected => {
					if (selected == 'proceed') {
						// HE SAID YES!
						this.isProcessing = true
						axios.delete(this.dataSource + '/' + itemID).then(response => {
							if (response.status) {
								// this.$router.push({name: 'Levantamiento de inventario', params: {id: response.data.id}})
								this.loadUp().then(() => {
									this.$bvModal.hide('main-card-modal')
									this.isProcessing = false
								})
							} else {
								this.isProcessing = false
							}
						})
					}
				})
			} else {
				swal({
					text: 'Para poder eliminar un registro, éste debe especifcar un ID de eliminación.',
				})
			}
		},
		// PREPARA LO QUE TENGAS QUE PREPARAR PARA SUBIR AL SERVIDIOR, COMO AGREGAR VALIDACIONES, DATOS O PROPIEDADES DINÁMICAS, ETC.
		// DEVULEVE FALSO PARA DETENER EL ENVÍO
		getDataForSubmit() {
			let data = _.cloneDeep(this.form);
			data.usuarios_id = this.usuariosId,
				_.forEach([], keyRequired => {
					if (typeof data == 'object' && !data[keyRequired]) {
						swal({
							text: 'Asegúrate de llenar todos los campos con *.',
						})
						data = false;
					}
				})
			return data;
		},
		cleanFormAndOpenModal() {
			this.form = _.cloneDeep(formArchetype)
			this.$bvModal.show('main-card-modal')
		},
		setupItemInFormAndOpenModal(item) {
			this.form = _.cloneDeep(item)
			this.$bvModal.show('main-card-modal')
		},
		async refresh() {
			this.isLoading = true;
			await this.loadUp()
			this.isLoading = false;
		},
		async loadUp() {
			const response = await axios.get(this.sourceURI)
			if (typeof response.data.rows != undefined) {
				this.data.rows = response.data.rows
				this.data.count = response.data.count
			}
		},
		hidden() {
			this.form = _.cloneDeep(formArchetype)
		},
		store() {
			let data2send = this.getDataForSubmit()
			if (!!data2send) {
				this.isProcessing = true
				axios.post('presentacion/update', data2send).then(response => {
					if (response.status) {
						// this.$router.push({name: 'Levantamiento de inventario', params: {id: response.data.id}})
						this.loadUp().then(() => {
							this.$bvModal.hide('main-card-modal')
							this.isProcessing = false
						})
					} else {
						this.isProcessing = false
					}
				})
			}
		},
		onSubmit($evnt) {
			$evnt.preventDefault();
			this.store()
		},
		async loadUpUi() {
			const responseData = await uiAsidesFetch()
			_.forEach(responseData, (data, key) => {
				this[key] = data
			})
		}
	},
	async beforeMount() {
		this.isLoading = true;
		await Promise.all([
			this.loadUp(),
			this.loadUpUi()
		])
		this.isLoading = false;
	},
	watch: {
		'data.page'() {
			this.isLoading = true;
			this.loadUp().then(() => {
				this.isLoading = false;
			})
		}
	}
}

</script>


<style lang="less">
#PresentacionesIndexMainCard {
	.product-img {
		@SIZE: 5em;
		width: @SIZE;
		height: @SIZE;
		// margin-top: -1.5em;

		// padding-top: 1em;
		IMG {
			width: 100%;
			height: 100%;
			border-radius: .5em;
			object-fit: cover;
			border: 1px solid lightgray;
			// position: relative;
			// top: .75em;
		}
	}
}
</style>